'use client';

import { Badge, Skeleton } from '@common/components';
import { SvgBookmarkFill, SvgChatbubbleTypingFill } from '@common/icon';
import { assert, cn } from '@core/utils';

import { BOOKMARK_TYPE } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { ROUTES, SELECTOR } from '@/constants';
import { useBookmark } from '@/hooks/useBookmark';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import {
  CONTENT_CATEGORY,
  GTM_EVENT_TYPE,
  trackGtmEvent,
} from '@/utils/analytics/gtm';
import { extractDate } from '@/utils/extractDate';

export type PolicyCardProps = {
  isLoading?: boolean;
  id?: number;
  idIdx?: string;
  imageUrl?: string;
  name?: string;
  endAt?: string;
  dday?: string;
  benefit?: string;
  commentCount?: number;
  border?: boolean;
  isBookmarked?: boolean;
  bookmarkCount?: number;
  agencyId?: number;
  agency?: string;
  variant?: 'default' | 'scrap';
  /** 클릭 이벤트가 있을 때에는 기본 기능인 링크 이동을 방지 */
  onClick?: () => void;
};

const BOOKMARK = '스크랩';
const COMMENT = '댓글';

export const PolicyCard = ({
  endAt,
  name,
  dday,
  benefit,
  bookmarkCount,
  commentCount,
  isBookmarked = false,
  isLoading,
  onClick,
  idIdx,
  border,
  imageUrl,
  id,
  agency,
  variant = 'default',
}: PolicyCardProps) => {
  const router = useCustomRouter();

  const date = endAt ? extractDate(endAt) : undefined;

  const href = ROUTES.POLICY_DETAIL.pathname({
    idIdx: idIdx ?? '',
  });

  const { bookmark } = useBookmark();

  const isScrap = variant === 'scrap';

  return (
    <CustomLink
      className={cn(
        'flex flex-col',
        isScrap
          ? 'gap-16 px-20 py-16'
          : 'gap-12 rounded-small bg-Gray-white px-15 pb-14 pt-16',
        !isScrap && border && 'border border-Gray-300',
      )}
      href={href}
      onClick={(e) => {
        trackGtmEvent(GTM_EVENT_TYPE.SELECT_CONTENT, {
          content_name: name ?? '',
          content_category: CONTENT_CATEGORY.POLICY,
          content_id: id?.toString() ?? '',
        });

        if (isLoading || onClick) e.preventDefault();
        onClick?.();
      }}
    >
      <section className="flex items-center gap-12">
        {isLoading ? (
          <Skeleton className="size-52 rounded-medium" />
        ) : (
          <Image
            alt={name || '정책 이미지'}
            className={cn(
              'size-52 rounded-large border border-Gray-300',
              isScrap ? 'object-contain' : 'object-cover p-6',
            )}
            height={52}
            src={imageUrl}
            width={52}
          />
        )}
        {isLoading ? (
          <Skeleton className="h-19 w-80" />
        ) : (
          <div className="line-clamp-2">
            {dday && !isScrap ? (
              <Badge className="mr-8 inline-block h-23 text-R-400">
                {dday}
              </Badge>
            ) : null}
            <h4 className="inline break-keep font-500 typo-body3">{name}</h4>
          </div>
        )}
      </section>
      {isLoading ? (
        <div className="flex justify-between gap-16">
          <div className="flex flex-1 items-center gap-10">
            <Skeleton className="h-19 w-100" />
            <Skeleton className="h-19 w-80" />
          </div>
          <Skeleton className="h-19 w-28" />
          <Skeleton className="h-19 w-28" />
        </div>
      ) : (
        <footer className="flex justify-between gap-8">
          {isLoading ? (
            <div className="flex flex-1 items-center gap-10">
              <Skeleton className="h-19 w-100" />
              <Skeleton className="h-19 w-80" />
            </div>
          ) : (
            <div className="flex flex-1 items-center gap-5 overflow-hidden text-nowrap text-Gray-600 typo-caption1">
              {benefit ? <p className="truncate">{benefit}</p> : null}
              {benefit && endAt ? (
                <div className="h-10 w-1 bg-Gray-400" />
              ) : null}
              {endAt ? <time dateTime={date}>{endAt}</time> : null}
            </div>
          )}
          {typeof commentCount === 'number' ? (
            <button
              aria-label={COMMENT}
              className="group gap-4 text-Gray-700 flex-center typo-caption1"
              title={COMMENT}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                router.push(href + '#' + SELECTOR.COMMENT_LIST_TOP);
              }}
            >
              <SvgChatbubbleTypingFill
                aria-label={COMMENT}
                className="size-16 text-Gray-400 duration-75 group-active:scale-75"
              />
              {commentCount < 1_000 ? commentCount : '999+'}
            </button>
          ) : null}
          {typeof bookmarkCount === 'number' ? (
            <button
              aria-label={BOOKMARK}
              className="group gap-4 text-Gray-700 flex-center typo-caption1"
              title={BOOKMARK}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                assert(id);

                bookmark({
                  bookmarkType: BOOKMARK_TYPE.POLICY,
                  contentId: id,
                  isBookmarked,
                  idx: idIdx,
                  contentName: name,
                  policyAgency: agency,
                });
              }}
            >
              <SvgBookmarkFill
                aria-label={BOOKMARK}
                className={cn(
                  'size-16 text-Gray-400 transition-all duration-75 group-active:scale-75',
                  isBookmarked && 'text-B-500',
                )}
              />
              {bookmarkCount < 1_000 ? bookmarkCount : '999+'}
            </button>
          ) : null}
        </footer>
      )}
    </CustomLink>
  );
};
